/**
 * Mock payment processor (NM-225/226). The app has no real billing integration
 * yet, so — exactly like the dev sign-in chooser stands in for Google OAuth
 * (`dev-auth.ts`) — this stands in for a real card processor so the upgrade flow
 * can be built and tested end to end on local + beta. It is enabled only where
 * `mockBilling` is on (never a real production stack); a real processor would
 * replace `chargeCard` behind the same route.
 *
 * The outcome is driven entirely by the **card number**, so a tester can exercise
 * every branch without any real money or provider. The numbers are the widely
 * known Stripe test-card values, chosen so they are familiar and unmistakably
 * fake. Everything is pure and synchronous — no network, no state.
 */

/** The one card that succeeds. */
export const SUCCESS_CARD = '4242424242424242';

/** Card number → the specific decline it simulates. Anything else that is a valid
 *  16-digit number is a generic decline; a malformed number is a validation error.
 *  A Map (not a record) so a lookup is `… | undefined` — the number is untrusted. */
const DECLINE_CARDS = new Map<string, { code: DeclineCode; message: string }>([
  ['4000000000000002', { code: 'declined', message: 'Your card was declined.' }],
  ['4000000000009995', { code: 'insufficient_funds', message: 'Your card has insufficient funds.' }],
  ['4000000000000069', { code: 'expired_card', message: 'Your card has expired.' }],
  [
    '4000000000000119',
    { code: 'processing_error', message: 'Something went wrong processing your card. Please try again.' },
  ],
]);

export type DeclineCode = 'declined' | 'insufficient_funds' | 'expired_card' | 'processing_error';
export type ChargeFailureCode = DeclineCode | 'invalid_number';

export type ChargeResult = { ok: true } | { ok: false; code: ChargeFailureCode; message: string };

/**
 * "Charge" a card. Spaces and dashes in the entered number are ignored; the number
 * must be exactly 16 digits or it is rejected as malformed before any decline is
 * considered (a real form would validate the format client-side too, but the server
 * is the one that must not trust it). The success card succeeds; the four known
 * decline cards return their specific reason; any other valid-format number is a
 * generic decline, so a random guess never silently "pays".
 */
export function chargeCard(rawNumber: string): ChargeResult {
  const digits = rawNumber.replace(/[\s-]/g, '');
  if (!/^\d{16}$/.test(digits)) {
    return { ok: false, code: 'invalid_number', message: 'Enter a valid 16-digit card number.' };
  }
  if (digits === SUCCESS_CARD) return { ok: true };
  const known = DECLINE_CARDS.get(digits);
  if (known !== undefined) return { ok: false, code: known.code, message: known.message };
  return { ok: false, code: 'declined', message: 'Your card was declined.' };
}

/** The test cards, for the checkout UI's "test cards" hint and the docs. */
export const MOCK_TEST_CARDS: { number: string; outcome: string }[] = [
  { number: SUCCESS_CARD, outcome: 'Payment succeeds' },
  { number: '4000000000000002', outcome: 'Declined' },
  { number: '4000000000009995', outcome: 'Insufficient funds' },
  { number: '4000000000000069', outcome: 'Expired card' },
  { number: '4000000000000119', outcome: 'Processing error' },
];
